import { useMemo, useState } from 'react';

import {
  EmptyState,
  InteractiveDropdownFilterInput,
  InteractiveDropdownFooter,
  InteractiveDropdownHeader,
  InteractiveDropdownSelect,
  InteractiveDropdownView,
  MaterialIcon,
} from '@components';
import { Button } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { routes } from '@routes';
import { CustomAnalyticsEvents } from '@utils/analytics';
import { useListSavedQueries } from '@Inventory/services/savedQueryQueries';
import { WITH_SAVED_QUERY_QUERY_PARAM } from '@AccessFlows/common/constants';

interface DropdownSavedQueriesViewProps {
  current: boolean;
  value?: string;
  isLoading: boolean;
  onSavedQueryChange: (value: string) => void;
  onBackClick?: () => void;
}

function useDropdownSavedQueriesView({ isLoading }: Pick<DropdownSavedQueriesViewProps, 'isLoading'>) {
  const { savedQueries, isFetched: isSavedQueriesFetched } = useListSavedQueries();
  const [filter, setFilter] = useState('');

  const handleFilterChange = (val: string) => setFilter(val);

  const options = useMemo(() => {
    return savedQueries
      .map((savedQuery) => ({
        key: savedQuery.id,
        value: savedQuery.name,
      }))
      .filter((v) => `${v.key} ${v.value}`.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
  }, [savedQueries, filter]);

  const isFetched = useMemo(() => isSavedQueriesFetched && !isLoading, [isSavedQueriesFetched, isLoading]);

  return {
    isFetched,
    options,
    filter,
    handleFilterChange,
  };
}

export function DropdownSavedQueriesView({
  current,
  value,
  isLoading,
  onSavedQueryChange,
  onBackClick,
}: DropdownSavedQueriesViewProps) {
  const { isFetched, options, filter, handleFilterChange } = useDropdownSavedQueriesView({ isLoading });

  const { id: accessFlowId } = useParams();

  const editLinkQP = useMemo(() => {
    const params = new URLSearchParams();
    params.set(WITH_SAVED_QUERY_QUERY_PARAM, accessFlowId ?? true.toString());
    return params;
  }, [accessFlowId]);

  if (!isFetched) {
    return <InteractiveDropdownView current loading />;
  }

  return (
    <InteractiveDropdownView
      current={current}
      header={<InteractiveDropdownHeader title="Select access scope" onBackClick={onBackClick} />}
      filter={<InteractiveDropdownFilterInput value={filter} onChange={handleFilterChange} />}
      footer={
        options.length > 0 && (
          <InteractiveDropdownFooter>
            <Button
              component={Link}
              to={`${routes.Inventory.path}?${editLinkQP.toString()}`}
              variant="text"
              startIcon={<MaterialIcon symbol="add" />}
              data-trigger={CustomAnalyticsEvents.ACCESS_SCOPE_CREATE_INITIATED_IN_BUILDER}
            >
              Create New Access Scope
            </Button>
          </InteractiveDropdownFooter>
        )
      }
    >
      <InteractiveDropdownSelect<string>
        options={options}
        value={value}
        onChange={onSavedQueryChange}
        selectedFirst
        emptyState={
          <EmptyState
            imgSrc="/static/EmptyStateImages/not-found-illustration.svg"
            title="No access scopes created yet"
            action={
              <Button component={Link} to={`${routes.Inventory.path}?${editLinkQP.toString()}`} variant="contained">
                Create New Access Scope
              </Button>
            }
          />
        }
      />
    </InteractiveDropdownView>
  );
}
